'use client';

import NextImage from "next/image";
import { Image } from "@habacuscode/habacus_ui_component_library";
import habacusBlue from "@/assets/images/logos/habacus_blue.svg";

import GenericInfoBoxDisplay from "@/components/infoBox/GenericInfoBox";
import { ConfigBorder, Info, WizardConfig } from "@/types/configuration";
import { getDateTimeCache } from "@/utils/helpers";
import StageInfoBoxDisplay from "./StageInfoBox";
import { useEffect, useState } from "react";
import { retrieveParternshipLogo } from "@/utils/client/customizations";


export default function LeftColumn({
	wizardConfig,
	isResultPage,
	currentStepInfo,
	locale,
	appEnv,
	tPrivacy,
	habacusStaticUrl
}: {
	wizardConfig: WizardConfig;
	isResultPage: boolean;
	currentStepInfo: Info | null;
	locale: string;
	appEnv: string;
	tPrivacy: any;
	habacusStaticUrl: string;
}) {
	const [dateTimeCache, setDateTimeCache] = useState<string | null>(null);

	useEffect(() => {
		setDateTimeCache(getDateTimeCache('HH'));
	}, []);

	const getRightImageBorder = function (border: ConfigBorder | undefined): string {
		if (!border) {
			return '';
		}

		let className: string = 'border';

		if (border.top) {
			className += ` border-t-${border.top}`;
		} else {
			className += ` border-t-0`;
		}

		if (border.right) {
			className += ` border-r-${border.right}`;
		} else {
			className += ` border-r-0`;
		}

		if (border.bottom) {
			className += ` border-b-${border.bottom}`;
		} else {
			className += ` border-b-0`;
		}

		if (border.left) {
			className += ` border-l-${border.left}`;
		} else {
			className += ` border-l-0`;
		}

		return className;
	}

	return (
		<div className='wizard-main-container-boxes left-column'>
			{/* Main content */}
			<div className="flex-1 relative lg:h-full">
				<NextImage
					width={640}
					height={900}
					src={`${wizardConfig.parternship?.right?.background ?? `${habacusStaticUrl}/background/svg/background_default.svg`}?ts=${dateTimeCache}`}
					alt="Background"
					className={`absolute inset-0 w-full h-full object-cover ${getRightImageBorder(wizardConfig.parternship?.right?.border)}`}
					fetchPriority="high"
					loading="eager"
				/>

				{/* Content container */}
				<div className={`mx-auto lg:overflow-y-auto pt-12 md:pt-0 ${isResultPage ? 'mb-32' : 'mb-24'} md:mb-0 gap-8 content-container relative w-full flex flex-col items-center xl:py-16 lg:py-4 lg:gap-4 px-8 place-content-between`}>
					{/* Logo */}
					<div className="text-navy-900 text-6xl font-bold hidden md:block">
						<div className="flex justify-left">
							<Image
								className="object-contain self-center max-w-[200px] max-h-[100px]"
								showImageTitle={false}
								image={wizardConfig?.habacus?.logo ? `${wizardConfig.habacus.logo}?ts=${dateTimeCache}` : habacusBlue.src}
								alt='Habacus'
								link='#'
								width={"200px"}
								height={"100px"}
							/>
						</div>

						{wizardConfig.parternship && (
							<>
								<div className={`my-1 w-full text-center text-sm font-bold ${wizardConfig.habacus?.left?.mode == "dark" ? 'text-white' : ''}`}>x</div>
								{retrieveParternshipLogo(wizardConfig.parternship, dateTimeCache)}
							</>
						)}
					</div>

					{
						appEnv === 'staging' && (
							<StageInfoBoxDisplay language={locale} />
						)
					}

					{currentStepInfo && (
						<GenericInfoBoxDisplay info={currentStepInfo} language={locale} />
					)}

					{/* Footer links */}
					<div className="flex gap-8 mb-3 text-blue-950 font-medium text-xs md:text-sm underline">
						<a href={tPrivacy.raw('privacy').url} target="_blank" rel="noopener noreferrer" className={`${wizardConfig.habacus?.left?.mode == "dark" ? 'text-white' : ''}`}>{tPrivacy.raw('privacy').label}</a>
						<a href={tPrivacy.raw('transparency').url} target="_blank" rel="noopener noreferrer" className={`${wizardConfig.habacus?.left?.mode == "dark" ? 'text-white' : ''}`}>{tPrivacy.raw('transparency').label}</a>
						<a href={tPrivacy.raw('dataProcessing').url} target="_blank" rel="noopener noreferrer" className={`${wizardConfig.habacus?.left?.mode == "dark" ? 'text-white' : ''}`}>{tPrivacy.raw('dataProcessing').label}</a>
						{/* <a href="https://habacus.com" className="text-blue-950 font-medium underline" target="_blank" rel="noopener noreferrer">Guida interattiva</a> */}
					</div>
				</div>
			</div>
		</div>
	);
}